import React, { useEffect, useState, useRef } from "react";
import {
  Box, Flex, Icon, Link as ChakraLink, Badge, Text, Heading, Button,
  HStack, VStack, useColorModeValue, useBreakpointValue,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  Tabs, TabList, TabPanels, Tab, TabPanel, Tooltip,
} from "@chakra-ui/react";
import { Document, Page, pdfjs } from "react-pdf";
import axios from "axios";
import { FaUser, FaClock, FaMapMarkerAlt, FaVideo, FaBook } from "react-icons/fa";
import { utcTimeToLocalPretty } from '../../utils/dates';
import { Batch, StudentInfo, StudentBatchTree, StudentLessonNode, StudentTermNode } from "../Common/types";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

type ViewKind = "summary" | "content" | "assignment";

interface CourseDashboardPanelProps {
  batch: Batch;
  tree?: StudentTermNode[];
}

// One course's entire experience — strip, tree, and inline viewer — self-contained
// so each Tab panel owns its own selection/viewer state independently.
const CourseDashboardPanel: React.FC<CourseDashboardPanelProps> = ({ batch, tree }) => {
  const [selectedLesson, setSelectedLesson] = useState<StudentLessonNode | null>(null);
  const [activeView, setActiveView] = useState<ViewKind | null>(null);
  const [loadingView, setLoadingView] = useState<ViewKind | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfTitle, setPdfTitle] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(window.innerWidth * 0.6);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const stripBg = useColorModeValue("blue.50", "blue.900");
  const treeBg = useColorModeValue("gray.50", "gray.800");
  const viewerBg = useColorModeValue("gray.100", "gray.700");
  const hoverBg = useColorModeValue("gray.100", "gray.700");
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const headlineColor = useColorModeValue("teal.600", "teal.100");

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Reset the viewer whenever a different lesson is picked in the tree —
  // prevents a stale PDF lingering under a newly-selected lesson.
  useEffect(() => {
    setPdfUrl((prevUrl) => {
      if (prevUrl) URL.revokeObjectURL(prevUrl);
      return null;
    });
    setPdfTitle(null);
    setActiveView(null);
    setNumPages(null);
  }, [selectedLesson?.id]);

  const streamEndpointFor = (view: ViewKind, lessonId: number) => {
    if (view === "summary") return `/stream-summary-content/${lessonId}/`;
    if (view === "content") return `/stream-full-content/${lessonId}/`;
    return `/stream-assignment-content/${lessonId}/`;
  };

  const handleViewLesson = async (lesson: StudentLessonNode, view: ViewKind) => {
    const urlForView = view === "summary" ? lesson.summary_url : view === "content" ? lesson.content_url : lesson.assignment_url;
    if (!urlForView) return;

    setActiveView(view);
    setLoadingView(view);
    setPdfTitle(
      `${view === "summary" ? "Summary" : view === "content" ? "Lesson" : "Assignment"} — ${lesson.title}`
    );

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "https://default-url.com";
      const response = await fetch(`${apiUrl}${streamEndpointFor(view, lesson.id)}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!response.ok) throw new Error(`Failed to fetch ${view} PDF`);
      const pdfBlob = await response.blob();
      setPdfUrl(URL.createObjectURL(pdfBlob));
    } catch (error) {
      console.error(`Error fetching ${view} PDF:`, error);
    } finally {
      setLoadingView(null);
    }
  };

  return (
    <Box>
      {/* Top strip — course/batch identity and logistics */}
      <Box bg={stripBg} borderRadius="lg" p={5} boxShadow="md" mb={4}>
        <Heading size="md" mb={3} color={headlineColor}>{batch.course_title}</Heading>
        <Flex wrap="wrap" gap={5}>
          <HStack>
            <Icon as={FaUser} color="blue.500" />
            <Text fontSize="sm"><strong>Tutor:</strong> {batch.tutor_name || "Not Assigned"}</Text>
          </HStack>
          <HStack>
            <Icon as={FaClock} color="orange.500" />
            <Text fontSize="sm">
              {utcTimeToLocalPretty(batch.start_time)} – {utcTimeToLocalPretty(batch.end_time)}
            </Text>
          </HStack>
          <HStack>
            <Icon as={FaMapMarkerAlt} color="red.500" />
            <Text fontSize="sm">{batch.location || "Online"}</Text>
          </HStack>
          {batch.meeting_link && (
            <HStack>
              <Icon as={FaVideo} color="green.500" />
              <ChakraLink href={batch.meeting_link} color="blue.500" isExternal fontSize="sm">
                Join Meeting
              </ChakraLink>
            </HStack>
          )}
          <Badge colorScheme="purple" alignSelf="center">{batch.frequency}</Badge>
          <Badge colorScheme={batch.is_active ? "green" : "red"} alignSelf="center">
            {batch.is_active ? "Active" : "Inactive"}
          </Badge>
        </Flex>
        <Text fontSize="xs" color={mutedText} mt={2}>
          {batch.start_date} – {batch.end_date}
        </Text>
      </Box>

      {/* Term / lesson tree */}
      <Box bg={treeBg} borderRadius="lg" p={3} mb={4}>
        {(!tree || tree.length === 0) && (
          <Text fontSize="sm" color={mutedText} p={2}>No lessons available yet.</Text>
        )}
        <Accordion allowMultiple defaultIndex={[0]}>
          {tree?.map((term) => (
            <AccordionItem key={term.id} border="none">
              <AccordionButton borderRadius="lg" _hover={{ bg: hoverBg }} px={3} py={3}>
                <Box flex="1" textAlign="left">
                  <Text fontSize="sm" fontWeight="semibold">{term.title}</Text>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pt={1} pb={2} px={1}>
                <VStack align="stretch" spacing={1}>
                  {term.lessons.length === 0 && (
                    <Text fontSize="xs" color={mutedText} pl={4}>No lessons yet.</Text>
                  )}
                  {term.lessons.map((lesson) => {
                    const hasAny = !!lesson.summary_url || !!lesson.content_url || !!lesson.assignment_url;
                    const isSelected = selectedLesson?.id === lesson.id;

                    const lessonRow = (
                      <Button
                        key={lesson.id}
                        variant="ghost"
                        size="sm"
                        justifyContent="flex-start"
                        pl={6}
                        borderRadius="lg"
                        bg={isSelected ? hoverBg : "transparent"}
                        _hover={{ bg: hasAny ? hoverBg : "transparent" }}
                        isDisabled={!hasAny}
                        opacity={hasAny ? 1 : 0.5}
                        cursor={hasAny ? "pointer" : "not-allowed"}
                        w="100%"
                        onClick={() => setSelectedLesson(lesson)}
                      >
                        <HStack spacing={2}>
                          <FaBook size={12} />
                          <Text fontSize="xs" noOfLines={1}>{lesson.title}</Text>
                        </HStack>
                      </Button>
                    );

                    return (
                      <Box key={lesson.id}>
                        {hasAny ? lessonRow : (
                          <Tooltip label="Content not available yet" placement="right" hasArrow>
                            <Box>{lessonRow}</Box>
                          </Tooltip>
                        )}

                        {isSelected && (
                          <HStack spacing={2} pl={12} pt={1} pb={2}>
                            <Button
                              size="xs"
                              colorScheme={activeView === "summary" ? "blue" : "gray"}
                              variant={activeView === "summary" ? "solid" : "outline"}
                              isDisabled={!lesson.summary_url}
                              isLoading={loadingView === "summary"}
                              onClick={() => handleViewLesson(lesson, "summary")}
                            >
                              Summary
                            </Button>
                            <Button
                              size="xs"
                              colorScheme={activeView === "content" ? "green" : "gray"}
                              variant={activeView === "content" ? "solid" : "outline"}
                              isDisabled={!lesson.content_url}
                              isLoading={loadingView === "content"}
                              onClick={() => handleViewLesson(lesson, "content")}
                            >
                              Full Lesson
                            </Button>
                            <Button
                              size="xs"
                              colorScheme={activeView === "assignment" ? "purple" : "gray"}
                              variant={activeView === "assignment" ? "solid" : "outline"}
                              isDisabled={!lesson.assignment_url}
                              isLoading={loadingView === "assignment"}
                              onClick={() => handleViewLesson(lesson, "assignment")}
                            >
                              Assignment
                            </Button>
                          </HStack>
                        )}
                      </Box>
                    );
                  })}
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </Box>

      {/* Content viewer — always below the tree, never beside it */}
      {pdfUrl && (
        <Box ref={containerRef} bg={viewerBg} borderRadius="lg" p={4} boxShadow="md">
          <Text fontWeight="bold" mb={2}>{pdfTitle}</Text>
          <Document file={pdfUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages || 0)}>
            {Array.from(new Array(numPages ?? 0), (_, index) => (
              <Box key={index} p={4} display="flex" justifyContent="center">
                <Page pageNumber={index + 1} width={containerWidth - 32} renderTextLayer={false} renderAnnotationLayer={false} />
              </Box>
            ))}
          </Document>
        </Box>
      )}
    </Box>
  );
};

const StudentBatches = () => {
  const [batchs, setBatchs] = useState<Batch[]>([]);
  const [batchTree, setBatchTree] = useState<StudentBatchTree[]>([]);
  const [currentStudent, setCurrentStudent] = useState<StudentInfo>();

  const textColor = useColorModeValue("blue.800", "blue.50");
  const bannerColor = useColorModeValue("yellow.100", "yellow.700");

  const showBanner = useBreakpointValue({ base: false, md: true });

  useEffect(() => {
    const fetchCurrentStudent = async () => {
      try {
        const apiurl = process.env.REACT_APP_API_URL || "https://default-url.com";
        const response = await axios.get(`${apiurl}/students-info`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setCurrentStudent(response.data);
      } catch (error) {
        console.error("Error fetching Student Info:", error);
      }
    };
    fetchCurrentStudent();
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      if (!currentStudent) return;
      try {
        const apiurl = process.env.REACT_APP_API_URL || "https://default-url.com";
        const [batchesRes, treeRes] = await Promise.all([
          axios.get(`${apiurl}/students/${currentStudent.student_id}/batch-details`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
          axios.get(`${apiurl}/students/${currentStudent.student_id}/batch-lesson-tree`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
        ]);
        setBatchs(batchesRes.data);
        setBatchTree(treeRes.data);
      } catch (error) {
        console.error("Failed to load course dashboard data", error);
      }
    };
    fetchAll();
  }, [currentStudent]);

  return (
    <Box>
      {showBanner && (
        <Box bgColor={bannerColor} p={4} height={"70px"}>
          <HStack>
            <Heading size={"sm"}>Hello {currentStudent?.givenName} ! </Heading>
            <Text fontWeight={"normal"} fontSize={"md"}>
              Your courses, meeting links, and lesson content — all in one place.
            </Text>
          </HStack>
        </Box>
      )}
      {!showBanner && (
        <Box p={4} bgColor={bannerColor} height={"50px"}>
          <VStack>
            <Text fontWeight={"semibold"} fontSize={"md"}>Course Dashboard</Text>
          </VStack>
        </Box>
      )}

      <Box as="main" mx="auto" maxWidth="1200px" p={4} color={textColor}>
        {batchs.length === 0 && (
          <Text fontSize="sm" color="gray.500">You're not enrolled in any courses yet.</Text>
        )}

        {batchs.length > 0 && (
          <Tabs isLazy variant="soft-rounded" colorScheme="blue">
            <TabList flexWrap="wrap" mb={4}>
              {batchs.map((batch) => (
                <Tab key={batch.id}>{batch.course_title}</Tab>
              ))}
            </TabList>
            <TabPanels>
              {batchs.map((batch) => {
                const treeForBatch = batchTree.find((t) => t.batch_id === batch.id);
                return (
                  <TabPanel key={batch.id} px={0}>
                    <CourseDashboardPanel batch={batch} tree={treeForBatch?.terms} />
                  </TabPanel>
                );
              })}
            </TabPanels>
          </Tabs>
        )}
      </Box>
    </Box>
  );
};

export default StudentBatches;